import * as React from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { SkeletonTheme } from "react-loading-skeleton";
import { useDarkMode } from "./general/useDarkMode";
import { ToastProvider } from "./general/Toast";
import { useIFrame, useQuery } from "./general/Helpers";
import { getColor } from "./utils/Theme";
import { ImmersiveReaderView } from "./ImmersiveReaderView";
import Gallery from "./components/Gallery";
import Shell from "./shell/Shell";

/**
 * Application root.
 *
 * Everything except the Immersive Reader lives inside the shell. Immersive Reader stays outside it
 * because it is opened as a bare embedded view (typically in an iframe or a Teams tab) where a
 * sidebar and header would be wrong.
 *
 * Auth is a server-side Cookie/OIDC session shared with the Razor shell that hosts this bundle, so
 * there is no login route and no protected-route component here: an unauthenticated API call is
 * bounced to /Account/SignIn by the axios interceptor in general/HttpClient.
 */

function App() {
    const [isDarkMode, toggleDarkMode] = useDarkMode();
    const inIFrame = useIFrame();
    const query = useQuery();

    // A host that embeds this app (Teams, an iframe in the legacy system) can pin the theme with
    // ?darkmode=true|false so the embedded view matches its container rather than the user's own
    // last choice.
    React.useEffect(() => {
        const requested = query.get("darkmode");
        if (requested !== "true" && requested !== "false") return;

        const wantsDark = requested === "true";
        if (wantsDark !== isDarkMode) {
            toggleDarkMode && toggleDarkMode();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inIFrame, toggleDarkMode]);

    return (
        <div className="h-full w-full bg-canvas font-sans text-text">
            <SkeletonTheme baseColor={getColor("background-back", isDarkMode)} highlightColor={getColor("background-front", isDarkMode)}>
                <ToastProvider>
                    <BrowserRouter>
                        <Routes>
                            <Route path="/immersiveReader" element={<ImmersiveReaderView />} />
                            {/*
                              The component gallery sits outside the shell deliberately. It renders only
                              static sample content and makes no API calls, so keeping it out of the
                              session-gated shell means the design system can be reviewed - in both
                              themes - without a working sign-in or a reachable backend.
                            */}
                            <Route path="/dev/components" element={<Gallery />} />
                            <Route path="/*" element={<Shell />} />
                        </Routes>
                    </BrowserRouter>
                </ToastProvider>
            </SkeletonTheme>
        </div>
    );
}

export default App;
